/**
 * Encode an AudioBuffer-like object as a 16-bit PCM WAV Blob.
 * Uses `any` / global lookups because project tsconfig lib is es6-only (no DOM).
 */

function getBlobCtor(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  const win = g.window || g;
  return win.Blob || g.Blob;
}

export function audioBufferToWav(buffer: any): any {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  const samples = interleave(buffer);
  const dataLength = samples.length * (bitDepth / 8);
  const headerLength = 44;
  const arrayBuffer = new ArrayBuffer(headerLength + dataLength);
  const view = new DataView(arrayBuffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
  view.setUint16(32, numChannels * (bitDepth / 8), true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  floatTo16BitPCM(view, 44, samples);

  const BlobCtor = getBlobCtor();
  return new BlobCtor([arrayBuffer], { type: 'audio/wav' });
}

function interleave(buffer: any): Float32Array {
  const numChannels = buffer.numberOfChannels;
  if (numChannels === 1) {
    return buffer.getChannelData(0);
  }
  const length = buffer.length * numChannels;
  const result = new Float32Array(length);
  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) {
    channels.push(buffer.getChannelData(c));
  }
  let offset = 0;
  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numChannels; c++) {
      result[offset++] = channels[c][i];
    }
  }
  return result;
}

function floatTo16BitPCM(view: DataView, offset: number, input: Float32Array) {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

export function pickRecorderMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  try {
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    const MediaRec = win.MediaRecorder;
    if (!MediaRec || typeof MediaRec.isTypeSupported !== 'function') {
      return '';
    }
    for (let i = 0; i < candidates.length; i++) {
      if (MediaRec.isTypeSupported(candidates[i])) {
        return candidates[i];
      }
    }
  } catch {
    return '';
  }
  return '';
}

export function defaultRecordFilename(): string {
  const d = new Date();
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `record-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${pad(m)}:${pad(s)}`;
}

export function supportsDisplayMediaAudio(): boolean {
  try {
    const g: any = typeof global !== 'undefined' ? global : {};
    const nav = (g.window && g.window.navigator) || g.navigator;
    return !!(nav && nav.mediaDevices && typeof nav.mediaDevices.getDisplayMedia === 'function');
  } catch {
    return false;
  }
}
